export const appLoaded = () => ({ event: "admin.app_loaded" });

// user successfully registers a new account
// `role` is the role they selected (Team/Individual, RTE/STE, etc.).
// Role values can contain slashes which fail kebab validation, so replace
// them with dashes before the analytics layer kebab-cases the value.
export const userRegistered = (role) => ({
  event: "admin.register_page.user_registered",
  ...(role ? { role: role.replace(/\//g, "-") } : {}),
});

// user clicks an OAuth provider button on the register page
// (painted-door experiment — no real OAuth flow yet)
export const registerOAuthClicked = (provider) => ({
  event: "admin.register_page.oauth_clicked",
  provider,
});

// user landed on the register page
export const registerPageSeen = () => ({ event: "admin.register_page.page_seen" });

// user finished a step of the register flow. Currently only fired for
// `step: "credentials"` on the credentials → company transition.
export const registerStepCompleted = (stepId) => ({
  event: "admin.register_page.step_completed",
  stepId,
});

// the "already registered with X" hint is shown beneath the email input.
export const registerEmailExistsShown = () => ({ event: "admin.register_page.email_exists_shown" });

// user clicked the "Log in" link on the register page (landed on /register but
// already has an account).
export const registerLoginClicked = () => ({ event: "admin.register_page.login_clicked" });

export const registerWelcomeBackSeen = () => ({
  event: "admin.register_page.welcome_back_seen",
});

export const registerWelcomeBackLoginClicked = () => ({
  event: "admin.register_page.welcome_back_login_clicked",
});

export const registerWelcomeBackCreateNewClicked = () => ({
  event: "admin.register_page.welcome_back_create_new_clicked",
});

// register attempt rejected by the backend. `code` is the backend error code
// (e.g. DUPLICATE_COMPANY, CAPTCHA_FAILED) or absent for generic failures.
export const registerSubmitFailed = (code) => ({
  event: "admin.register_page.submit_failed",
  ...(code ? { code } : {}),
});

// user landed on the post-registration onboarding home page
export const onboardingHomeSeen = () => ({ event: "admin.onboarding.home_seen" });

// user clicked "Set up my ART" on the onboarding home page, opening the wizard.
export const onboardingSetupArtClicked = () => ({ event: "admin.onboarding.setup_art_clicked" });

// user completed a step of the onboarding wizard. Currently only fired for
// `stepId: "art-teams"` on the ART/teams → planning-interval transition; the
// final step's completion is tracked as `onboardingPiCreated`.
export const onboardingStepCompleted = (stepId) => ({
  event: "admin.onboarding.step_completed",
  stepId,
});

// the onboarding wizard created the ART/teams/PI successfully.
// `withPreset` is true when the user kept a template selected.
export const onboardingPiCreated = (withPreset) => ({
  event: "admin.onboarding.pi_created",
  with_preset: withPreset,
});

// the onboarding success dialog was shown (PI created).
export const onboardingSuccessSeen = () => ({ event: "admin.onboarding.success_seen" });

// onboarding success dialog CTA click. `target` is
// "view-app" | "customize-session".
export const onboardingSuccessCtaClicked = (target) => ({
  event: "admin.onboarding.success_cta_clicked",
  target,
});

// user sent teammate invites from the onboarding success dialog.
// `count` is the number of new invitees added.
export const onboardingInvitesSent = (count) => ({
  event: "admin.onboarding.invites_sent",
  count,
});

// Home dashboard — PI sessions widget
// user clicked the "+" new-session button.
export const sessionsWidgetCreateClicked = () => ({
  event: "admin.sessions_widget.create_clicked",
});
// user opened a session from the list (row or kebab "Edit session").
export const sessionsWidgetSessionOpened = () => ({
  event: "admin.sessions_widget.session_opened",
});
// user clicked the "View all sessions" footer link.
export const sessionsWidgetViewAllClicked = () => ({
  event: "admin.sessions_widget.view_all_clicked",
});
// user clicked the kebab "Duplicate" action.
export const sessionsWidgetDuplicateClicked = () => ({
  event: "admin.sessions_widget.duplicate_clicked",
});

// Home dashboard — ALM connections widget
// user clicked the "+" add-connection button.
export const almConnectionsWidgetAddClicked = () => ({
  event: "admin.alm_connections_widget.add_clicked",
});
// user opened a connection from the list (row or kebab "Edit connection").
export const almConnectionsWidgetConnectionOpened = () => ({
  event: "admin.alm_connections_widget.connection_opened",
});
// user clicked the "View all connections" footer link.
export const almConnectionsWidgetViewAllClicked = () => ({
  event: "admin.alm_connections_widget.view_all_clicked",
});

// Home dashboard — recent ALM events widget
// user opened an event row (navigates to the connection's events page).
export const almEventsWidgetEventOpened = () => ({
  event: "admin.alm_events_widget.event_opened",
});

// user click on a link in the sidebar
export const sidebarItemClicked = (item) => ({
  event: "admin.sidebar.item_clicked",
  item,
});

// user click on prepare new PI button
export const safepiplanningsNewButtonClicked = () => ({
  event: "admin.safepiplannings_page.prepare_new_button_clicked",
});

// user clicks on the action menu edit button
export const safepiplanningsEditSessionButtonClicked = (trigger) => ({
  event: "admin.safepiplannings_page.session_action_menu_edit",
  ...(trigger ? { trigger } : {}),
});

// user opens the PI Details modal from a session row's "more actions" dropdown
export const safepiplanningsSessionDetailsClicked = () => ({
  event: "admin.safepiplannings_page.session_action_menu_details",
});

// user opens the "more actions" dropdown on a session row
export const safepiplanningsSessionActionMenuOpened = () => ({
  event: "admin.safepiplannings_page.session_action_menu_opened",
});

// user clicks duplicate on a session row.
// `trigger`: "action-toolbar" (quick icon) | "action-menu" (more-actions dropdown).
export const safepiplanningsDuplicateSessionClicked = (trigger) => ({
  event: "admin.safepiplannings_page.session_action_menu_duplicate",
  ...(trigger ? { trigger } : {}),
});

// user duplicates a session using the duplicate modal in the Safepiplannings Page
export const safepiplanningsDuplicateModalOptionSelected = (withIterations) => ({
  event: "admin.safepiplannings_page.duplicate_modal_option_selected",
  withIterations,
});

// user sees the duplicate session modal
export const safepiplanningsDuplicateModalSeen = () => ({
  event: "admin.safepiplannings_page.duplicate_modal_seen",
});

// user clicks on the action menu import button
export const safepiplanningsImportModalSeen = (modal) => ({
  event: "admin.safepiplannings_page.import_modal_seen",
  modal,
});

// user clicks on the action menu xlsx download button
export const safepiplanningsXlsxDownloadClicked = () => ({
  event: "admin.safepiplannings_page.session_action_menu_xlsx_download",
});

// user clicks on the action menu delete button
export const safepiplanningsSessionDeleteClicked = () => ({
  event: "admin.safepiplannings_page.session_action_menu_delete",
});
// user clicks on the action menu archive button
export const safepiplanningsSessionArchiveClicked = () => ({
  event: "admin.safepiplannings_page.session_action_menu_archive",
});

/**
 * User triggers an ALM status update from the Sessions page
 * @param {string} action - The action taken by the user:
 *        "restart" | "stop" | "force restart" | "fix"
 * @param {string} alm_tool - The ALM tool used by the session
 */
export const safepiplanningsUpdateALMStatusClicked = (action, alm_tool) => ({
  event: "admin.safepiplannings_page.update_alm_status_clicked",
  action,
  alm_tool,
});

// user opens the ALM sync status dropdown on a session row
export const safepiplanningsAlmStatusMenuOpened = (alm_tool) => ({
  event: "admin.safepiplannings_page.update_alm_status_menu_opened",
  alm_tool,
});

// users sees the "New Session" page
export const newPiplanningPageSeen = () => ({ event: "admin.new_piplanning.page_seen" });

// user completes a step in the "New Session" page
export const newPiplanningStepCompleted = (step) => ({
  event: "admin.new_piplanning.step_completed",
  step,
});

// user finish adding a new session
export const newPiplanningCompleted = () => ({ event: "admin.new_piplanning.completed" });

// user clicks "Start piplanning" on the "You're all set!" page
export const newPiplanningStartAppClicked = () => ({
  event: "admin.new_piplanning.start_app_clicked",
});

// user clicks "Back to PI Sessions" on the "You're all set!" page
export const newPiplanningBackToSessionsClicked = () => ({
  event: "admin.new_piplanning.back_to_sessions_clicked",
});

//  use clicks on the "Create new User" button on the Users page
export const usersPageCreateUserClicked = () => ({
  event: "admin.users_page.create_user_button_clicked",
});

// user sent invites from the global "Invite users" dialog.
// `count` is the number of new invitees; `source` is where the dialog was
// opened from — "users_page" | "quickstart".
export const usersInviteSent = (count, source) => ({
  event: "admin.users.invite_sent",
  count,
  source,
});

// when the users crates a new user on the "Create User" page
export const userCreated = (role) => ({
  event: "admin.create_user_page.user_created",
  role,
});

// user clicks on the "Create Team" button on the Teams tab
// on the Organization page
export const teamsPageCreateTeamButtonClicked = () => ({
  event: "admin.teams_page.create_new_button_clicked",
});

// when a new team is created by the user on the "Create Team" page
export const teamCreated = (withUserInvites) => ({
  event: "admin.create_team_page.team_created",
  withUserInvites,
});

// user clicks on the "Create ART" button on the ARTs tab
// on the Organization page
export const artsPageCreateNewClicked = () => ({
  event: "admin.arts_page.create_new_button_clicked",
});

// when a new ART is created by the user on the "Create ART" page
export const artCreated = () => ({ event: "admin.create_art_page.art_created" });

// user clicks on the "Create Solution Train" button on the
// Solution Trains tab on the Organization page
export const solutionTrainsCreateNewClicked = () => ({
  event: "admin.solution_trains_page.create_new_button_clicked",
});

// when a new Solution Train is created by the user on the
// "Create Solution Train" page
export const solutionTrainCreated = () => ({
  event: "admin.create_solution_train_page.solution_train_created",
});

// user clicks on the "Add new ALM connection" button
// on the ALM connections page
export const almConnectionsPageAddNewButtonClicked = () => ({
  event: "admin.alm_connections_page.create_new_button_clicked",
});

// the user sees the "ALM Connections" page
export const almConnectionsPageSeen = () => ({ event: "admin.alm_connections_page.page_seen" });

// when an ALM tool is succesfully connected
export const almConnectionCreated = (alm) => ({
  event: "admin.create_alm_connections_page.connection_created",
  alm,
});

// triggered when the user chooses an ALM tool and clicks on next on
// the "Create new ALM connection" page
export const createAlmConnectionsConnectToAlmStarted = (alm) => ({
  event: "admin.create_alm_connections_page.connect_started",
  alm,
});

/**
 * Triggered when the user clicks "Unlock with xxx Plan" (link to pricing page)
 */
export const upgradeCTAClicked = (source) => ({
  event: "admin.upgrade_dialog.cta_clicked",
  source,
});

/**
 * Triggered when the user clicks a "learn more" link in the upgrade dialog
 */
export const upgradeInfoLinkClicked = (source) => ({
  event: "admin.upgrade_dialog.info_link_clicked",
  source,
});

export const trialExtendClicked = () => ({ event: "admin.trial_extend.cta_clicked" });

// user dismissed the sidebar trial-extension card via the × button.
export const trialCardDismissed = () => ({ event: "admin.trial_card.dismissed" });

export const learnMoreOpened = () => ({ event: "admin.learn_more.opened" });

export const learnMoreItemClicked = ({ id, target }) => ({
  event: "admin.learn_more_item.clicked",
  id,
  target,
});

// user clicks "Create new sticker" in the Stickers section.
// `count_after` is the resulting sticker count (pre-emit count + 1).
export const stickerAdded = (countAfter) => ({
  event: "admin.session_stickers.sticker_added",
  count_after: countAfter,
});

export const stickerLabelEdited = () => ({ event: "admin.session_stickers.sticker_label_edited" });

// user picked a new icon/color and the change was applied (after the
// in-use check, where applicable). `is_first_assignment` is true when the
// previous icon was "none" (i.e. filling out a new sticker rather than
// swapping an existing one).
export const stickerIconChanged = (isFirstAssignment) => ({
  event: "admin.session_stickers.sticker_icon_changed",
  is_first_assignment: isFirstAssignment,
});

// user deleted a sticker row. `was_saved` is true when the sticker had
// already been saved on a previous visit (a real deletion), false when the
// user is removing a row they just added in this session (draft cleanup).
export const stickerDeleted = (wasSaved) => ({
  event: "admin.session_stickers.sticker_deleted",
  was_saved: wasSaved,
});

// user reordered stickers via drag-and-drop or the keyboard reorder mode.
export const stickerReordered = (method) => ({
  event: "admin.session_stickers.sticker_reordered",
  method,
});

// the in-use guard fired and the modal opened, blocking either a delete or
// an icon change. `referenced_sticky_count` is the number of existing
// stickies that prevented the change.
export const stickerChangeBlocked = ({ action, referencedStickyCount }) => ({
  event: "admin.session_stickers.sticker_change_blocked",
  action,
  referenced_sticky_count: referencedStickyCount,
});

export const sessionConfigSaved = (creating, page) => ({
  event: "admin.session_config.saved",
  flow: creating ? "creating" : "editing",
  page,
});

// The override warning modal was shown to the user (they hit save/next with
// config errors). Pairs with `sessionErrorsOverridden` to measure how often
// users proceed vs back out.
export const sessionErrorsOverrideShown = (creating, page) => ({
  event: "admin.session_config.errors_override_shown",
  flow: creating ? "creating" : "editing",
  page,
});

// User explicitly confirmed proceeding despite config errors (e.g. Jira sticky
// types that can't be created / are missing required fields in some projects).
export const sessionErrorsOverridden = (creating, page) => ({
  event: "admin.session_config.errors_overridden",
  flow: creating ? "creating" : "editing",
  page,
});

// QuickStart onboarding sidebar — opened (state transition false → true).
// `source` is what triggered it: "fab" | "user_menu" | "auto_trial".
export const quickstartOpened = (source) => ({
  event: "admin.quickstart.opened",
  source,
});

// QuickStart sidebar — user clicked the × close button.
export const quickstartClosed = () => ({ event: "admin.quickstart.closed" });

// QuickStart FAB — user clicked the × on the floating button to hide it.
export const quickstartButtonDismissed = () => ({
  event: "admin.quickstart.button_dismissed",
});

// QuickStart sidebar — user expanded an accordion step. `step` is the step id.
export const quickstartStepExpanded = (stepId) => ({
  event: "admin.quickstart.step_expanded",
  stepId,
});

// QuickStart sidebar — user clicked the CTA inside a step. `step` is the step id.
export const quickstartCtaClicked = (stepId) => ({
  event: "admin.quickstart.cta_clicked",
  stepId,
});

// QuickStart sidebar — user clicked a help link. `target` is "docs" | "support".
export const quickstartHelpClicked = (target) => ({
  event: "admin.quickstart.help_clicked",
  target,
});

// QuickStart sidebar — all four steps completed (progress reached 100%).
// Fires once per user (guarded by localStorage flag).
export const quickstartCompleted = () => ({ event: "admin.quickstart.completed" });

// Jira board select — user ticked or unticked "Show boards from all projects".
// `picker` is "kanban" | "team-board". `page` is the route name.
export const boardSelectShowAllToggled = (picker, page, checked) => ({
  event: "admin.board_select.show_all_toggled",
  picker,
  page,
  checked,
});
